import { useRef, useState } from 'react'
import { FORMATS, SERIES, type Channel, type Format, type Idea, type Series, type Status } from '../api'
import { FORMAT_CATALOG, orderChannels } from '../domain/formats'
import { CHANNEL_LABEL, FORMAT_LABEL, LOOP_ORDER, SERIES_LABEL, STATUS_LABEL } from '../domain/labels'
import { updateIdea } from '../state/ideas'
import { PickerSheet, type Option } from './Sheet'
import { SeriesDot } from './SeriesDot'
import { StatusIcon } from './StatusIcon'

export const STATUS_OPTIONS: Option<Status>[] = [...LOOP_ORDER, 'killed' as const].map((status) => ({
  value: status,
  label: STATUS_LABEL[status],
  icon: <StatusIcon status={status} />,
}))

export const SERIES_OPTIONS: Option<Series>[] = SERIES.map((series) => ({
  value: series,
  label: SERIES_LABEL[series],
  icon: <SeriesDot series={series} size={10} />,
}))

/** Short formats are most of the list, so only the others say which channel they are for. */
export const FORMAT_OPTIONS: Option<Format>[] = FORMATS.map((format) => {
  const spec: { channels: readonly Channel[]; volume?: true } = FORMAT_CATALOG[format]
  const elsewhere = spec.channels.filter((channel) => channel !== 'short')
  return {
    value: format,
    label: FORMAT_LABEL[format],
    hint: spec.volume ? 'volume' : elsewhere.map((c) => CHANNEL_LABEL[c]).join(', ') || undefined,
  }
})

export function changeStatus(idea: Idea, status: Status): void {
  if (status === idea.status) return
  void updateIdea(idea.id, { status }, { undo: `Moved to ${STATUS_LABEL[status]}` })
}

/**
 * Mark an idea posted on one channel, or take that back. The server may move
 * the status with it (every video channel posted is published); the undo
 * puts both back.
 */
export function setPosted(idea: Idea, channel: Channel, posted: boolean): void {
  if (idea.posted_to.includes(channel) === posted) return
  const next = posted ? [...idea.posted_to, channel] : idea.posted_to.filter((c) => c !== channel)
  const label = CHANNEL_LABEL[channel]
  void updateIdea(
    idea.id,
    { posted_to: orderChannels(next) },
    { undo: posted ? `Posted on ${label}` : `Unmarked as posted on ${label}` },
  )
}

/**
 * A status picker for whichever idea was tapped last.
 *
 * The idea is remembered past closing so the sheet does not go blank while it
 * slides away.
 */
export function useStatusPicker() {
  const [target, setTarget] = useState<Idea | null>(null)
  const last = useRef<Idea | null>(null)
  if (target) last.current = target
  const shown = target ?? last.current

  const element = (
    <PickerSheet
      open={target !== null}
      title="Status"
      options={STATUS_OPTIONS}
      value={shown?.status ?? null}
      onPick={(status) => shown && changeStatus(shown, status)}
      onClose={() => setTarget(null)}
    />
  )
  return { pick: setTarget, element }
}
